import { createElement } from 'react';
import { FaCalendarAlt, FaExclamation, FaInbox } from 'react-icons/fa';
import ProjectKanbanCard from './ProjectKanbanCard';

const formatDate = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * KanbanSwimlane
 * A horizontal row on the Kanban board representing either a milestone
 * or the "unassigned" bucket. Renders the swimlane header + one droppable
 * cell per column, each populated with ProjectKanbanCard children.
 *
 * Unifies the two ~150-line inline blocks previously duplicated in
 * ProjectDetailPage.jsx (milestone vs. unassigned). The unassigned variant
 * is toggled via the `isUnassigned` flag which changes:
 *   - the header (FaInbox + "Unassigned to Milestone")
 *   - the whole-row drop target (unparent vs. assign-to-milestone)
 *   - the per-cell drop handler (simple status change vs. milestone+status)
 */
const KanbanSwimlane = ({
    milestone,
    tasks,
    columns,
    draggedTask,
    getSubtasks,
    getUserName,
    getMilestoneIcon,
    isUnassigned = false,
    // Drag handlers — parent retains all state
    onDragStart,
    onDragOver,
    onDropOnMilestone,    // (e, milestoneId, statusValue)
    onDropStatus,          // (e, statusValue)  — used by unassigned cells
    onDropOnTask,
    onDropToUnparent,      // (e) — used by unassigned row-level drop
    onOpenDetail,
    onCreateSubtask,
    onEditTask
}) => {
    const accentColor = isUnassigned ? '#95a5a6' : (milestone?.color || '#f39c12');

    // Resolve milestone icon as a react element (not a component type) so the
    // static-components ESLint rule doesn't mistake our lookup for a factory.
    const milestoneIconEl = (!isUnassigned && getMilestoneIcon)
        ? createElement(getMilestoneIcon(milestone?.icon), { style: { color: accentColor } })
        : null;

    const rowDropHandler = isUnassigned
        ? (e) => onDropToUnparent(e)
        : (e) => onDropOnMilestone(e, milestone.milestoneId, null);

    return (
        <div className={`kanban-swimlane${isUnassigned ? ' unassigned-swimlane' : ''}`}>
            <div className="swimlane-header" style={{ borderLeftColor: accentColor }}>
                <div className="swimlane-label">
                    {isUnassigned ? (
                        <>
                            <FaInbox style={{ color: accentColor }} />
                            <span className="milestone-name">Unassigned to Milestone</span>
                        </>
                    ) : (
                        <>
                            {milestoneIconEl}
                            <span className="milestone-name">{milestone.name}</span>
                            {milestone.isCritical && (
                                <span className="critical-badge"><FaExclamation /></span>
                            )}
                            <span className={`milestone-status-badge status-${milestone.status?.toLowerCase().replace(' ', '-')}`}>
                                {milestone.status}
                            </span>
                            {milestone.targetDate && (
                                <span className="milestone-target-date">
                                    <FaCalendarAlt /> {formatDate(milestone.targetDate)}
                                </span>
                            )}
                        </>
                    )}
                    <span className="task-count">{tasks.length} tasks</span>
                </div>
            </div>
            <div
                className="swimlane-body"
                style={{ gridTemplateColumns: `200px repeat(${columns.length}, 1fr)` }}
                onDragOver={onDragOver}
                onDrop={rowDropHandler}
            >
                <div className="swimlane-spacer"></div>
                {columns.map(column => {
                    const columnTasks = tasks.filter(t => t.status === column.statusValue);
                    const wipExceeded = column.wipLimit > 0 && columnTasks.length > column.wipLimit;
                    const cellDropHandler = isUnassigned
                        ? (e) => onDropStatus(e, column.statusValue)
                        : (e) => onDropOnMilestone(e, milestone.milestoneId, column.statusValue);

                    return (
                        <div
                            key={column.id}
                            className={`kanban-cell ${draggedTask ? 'drop-target' : ''} ${wipExceeded ? 'wip-exceeded' : ''}`}
                            onDragOver={onDragOver}
                            onDrop={cellDropHandler}
                        >
                            {columnTasks.map(task => {
                                const subtasks = getSubtasks(task.id);
                                return (
                                    <ProjectKanbanCard
                                        key={task.id}
                                        task={task}
                                        subtasks={subtasks}
                                        isInCompletedColumn={column.isCompletedColumn}
                                        draggedTask={draggedTask}
                                        getUserName={getUserName}
                                        onDragStart={onDragStart}
                                        onDragOver={onDragOver}
                                        onDropOnTask={onDropOnTask}
                                        onSubtaskDragStart={(e, sub) => onDragStart(e, sub, true)}
                                        onOpenDetail={onOpenDetail}
                                        onCreateSubtask={onCreateSubtask}
                                        onEditTask={onEditTask}
                                    />
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default KanbanSwimlane;
